import { Type, type Static } from "@sinclair/typebox";
import { CancelledBy, Cents, LatLng, PaymentMethod, Place, PlaceInput, RideCategory, TripKind, TripStatus } from "./domain.ts";
import { Gender, TripTypeGroupView, TripTypeView, UnavailableReason, VehicleKind } from "./trip-types.ts";
import { PixPayment, TripCollectionView } from "./pix.ts";
import { PromoQuote, SignInMethod, TipView, TripStopView, TripStopsInput } from "./next.ts";

export { PixPayment };
import { DriverCard, OpenTrip, TripOffered } from "./events.ts";
import { RiderCard } from "./driver.ts";
import { TripDeliveryView } from "./delivery.ts";
import { SupportApp } from "./support.ts";

/** REST DTOs shared by the API (OpenAPI) and generated native clients (ADR 0006). */

/** The trip options a quote and a request share (all optional; absent = a plain ride now, by Pix). */
const TripOptions = {
  /** Paradas between pickup and dropoff, in order. */
  stops: Type.Optional(TripStopsInput),
  /** Levar pet (not on Moto): adds `pet.fee_cents`, offered only to drivers with "Aceito pets". */
  hasPet: Type.Optional(Type.Boolean()),
  /** Cupom, case-insensitive. */
  promoCode: Type.Optional(Type.String({ minLength: 2, maxLength: 40 })),
  /** Agendar: between `scheduled.min_lead_minutes` and `scheduled.max_days` ahead. */
  scheduledFor: Type.Optional(Type.String({ format: "date-time" })),
};

export const QuoteRequest = Type.Object(
  {
    pickup: PlaceInput, dropoff: PlaceInput, ...TripOptions,
    /** How the rider means to pay: types that don't take it come back `card_not_accepted`. */
    paymentMethod: Type.Optional(PaymentMethod),
  },
  { $id: "QuoteRequest" },
);
export const QuoteResponse = Type.Object(
  {
    distanceMeters: Type.Integer(),
    durationSeconds: Type.Integer(),
    polyline: Type.Union([Type.String(), Type.Null()]),
    /** Sections of Escolher corrida, in order; an option whose `type.groupId` is null goes last ("Outras opções"). */
    groups: Type.Array(TripTypeGroupView),
    /** In display order. Delivery types are included (`type.service: "delivery"`): the app opens the Envios form for them. */
    options: Type.Array(Type.Object({
      category: RideCategory,
      type: TripTypeView,
      /** What the rider pays (after the promo discount, pet fee included). */
      fareCents: Cents,
      pickupEtaSeconds: Type.Union([Type.Integer(), Type.Null()]),
      /** Before the discount, when a valid promo applies. */
      originalFareCents: Type.Optional(Cents),
      discountCents: Type.Optional(Cents),
      petFeeCents: Type.Optional(Cents),
      /** False when the option can't take the request (Moto with a pet); `unavailableReason` says why. */
      available: Type.Optional(Type.Boolean()),
      unavailableReason: Type.Optional(UnavailableReason),
    })),
    /** Set when the request had a promoCode. */
    promo: Type.Optional(Type.Union([PromoQuote, Type.Null()])),
  },
  { $id: "QuoteResponse" },
);

export const TripRequest = Type.Object(
  {
    category: RideCategory,
    pickup: PlaceInput,
    dropoff: PlaceInput,
    ...TripOptions,
    /** Default pix. card needs an eligible rider and an active saved card. */
    paymentMethod: Type.Optional(PaymentMethod),
    /** The saved card (default card when absent). */
    paymentMethodId: Type.Optional(Type.String()),
  },
  { $id: "TripRequest" },
);


/** A trip as one participant sees it. `boardingCode` is only present for the rider. */
export const TripView = Type.Object(
  {
    id: Type.String(),
    code: Type.String(),
    status: TripStatus,
    /** "ride" or "delivery" (Envios). */
    kind: TripKind,
    category: RideCategory,
    /** The trip's type as it is now (name, icon, vehicle kind, cancellation fee); labels never come from the id. */
    type: TripTypeView,
    role: Type.Union([Type.Literal("rider"), Type.Literal("driver")]),
    pickup: Place,
    dropoff: Place,
    distanceMeters: Type.Integer(),
    durationSeconds: Type.Integer(),
    polyline: Type.Union([Type.String(), Type.Null()]),
    fareCents: Cents,
    driverEarningsCents: Type.Optional(Cents),
    boardingCode: Type.Optional(Type.String()),
    driverId: Type.Union([Type.String(), Type.Null()]),
    /** Who is coming, once a driver accepted (name, car, plate, rating). */
    driver: Type.Union([DriverCard, Type.Null()]),
    /** Who is riding, for the driver only (first name and rating). */
    rider: Type.Optional(RiderCard),
    requestedAt: Type.String({ format: "date-time" }),
    acceptedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    promisedPickupAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    arrivedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    startedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    completedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    cancelledBy: Type.Union([CancelledBy, Type.Null()]),
    cancellationFeeCents: Type.Union([Cents, Type.Null()]),
    /** Deliveries only (null for rides). */
    delivery: Type.Union([TripDeliveryView, Type.Null()]),
    // Next features (docs/specs/next-features.md). Optional on the wire until every server path sets them.
    stops: Type.Optional(Type.Array(TripStopView)),
    scheduledFor: Type.Optional(Type.Union([Type.String({ format: "date-time" }), Type.Null()])),
    hasPet: Type.Optional(Type.Boolean()),
    petFeeCents: Type.Optional(Cents),
    paymentMethod: Type.Optional(PaymentMethod),
    /** Card trips: "Visa •••• 4242". */
    cardLabel: Type.Optional(Type.Union([Type.String(), Type.Null()])),
    /** Promo discount, already taken off fareCents. */
    discountCents: Type.Optional(Cents),
    tip: Type.Optional(Type.Union([TipView, Type.Null()])),
    /** The other participant turned on audio recording: show "Áudio pode ser gravado nesta corrida". */
    audioMayBeRecorded: Type.Optional(Type.Boolean()),
    /** Cash trips once completed: what to collect and how it went (null before completion and for other methods). */
    collection: Type.Optional(Type.Union([TripCollectionView, Type.Null()])),
  },
  { $id: "TripView" },
);
export type TripView = Static<typeof TripView>;

/** Card trips have no Pix to pay (pix null) and start searching (or scheduled) at once. */
export const TripCreated = Type.Object({ trip: TripView, pix: Type.Union([PixPayment, Type.Null()]) }, { $id: "TripCreated" });
/**
 * Why a rider cancelled, picked on the cancel sheet. `reason` stays a free string on the wire:
 * drivers and older app builds send their own codes (`rider_no_show`, `rider_requested`, …).
 */
export const RIDER_CANCEL_REASONS = [
  "taking_too_long", "driver_too_far", "driver_asked", "wrong_address", "changed_plans", "price_too_high", "switch_category", "other",
] as const;
export type RiderCancelReason = (typeof RIDER_CANCEL_REASONS)[number];
/**
 * Why a driver gave up a trip before boarding, picked on the driver's cancel sheet (required;
 * `driver_other` needs `feedback`). `too_many_passengers` is offered on rides, `package_issue` on deliveries. The
 * rider no-show after the free wait is `rider_no_show`, decided by the server.
 */
export const DRIVER_CANCEL_REASONS = [
  "rider_asked", "pickup_unreachable", "vehicle_problem", "unsafe", "too_many_passengers", "package_issue", "driver_other",
] as const;
export type DriverCancelReason = (typeof DRIVER_CANCEL_REASONS)[number];
export const CancelRequest = Type.Object(
  {
    reason: Type.Optional(Type.String({ maxLength: 200 })),
    /** What the rider wrote on the cancel sheet. */
    feedback: Type.Optional(Type.String({ maxLength: 500 })),
  },
  { $id: "CancelRequest" },
);
/** Another category for the same route, as the cancel sheet offers it. */
export const CategoryAlternative = Type.Object(
  {
    category: RideCategory,
    categoryName: Type.String(),
    /** The suggested type (icon, vehicle kind) for the "Trocar para …" card. */
    type: TripTypeView,
    fareCents: Cents,
    /** Available drivers of this category near the pickup (capped at `CANCEL_OPTIONS_DRIVER_CAP`). */
    nearbyDrivers: Type.Integer({ minimum: 0 }),
    pickupEtaSeconds: Type.Union([Type.Integer(), Type.Null()]),
  },
  { $id: "CategoryAlternative" },
);
/**
 * `GET /trips/{id}/cancel-options`: what the cancel sheet needs beyond the trip. `suggestion` is the
 * category with the most available drivers near the pickup, when it has more than the trip's own.
 */
export const CancelOptions = Type.Object(
  {
    category: RideCategory,
    nearbyDrivers: Type.Integer({ minimum: 0 }),
    suggestion: Type.Union([CategoryAlternative, Type.Null()]),
  },
  { $id: "CancelOptions" },
);
export type CancelOptions = Static<typeof CancelOptions>;
export const CANCEL_OPTIONS_DRIVER_CAP = 10;
/** Rides need the rider's 4-digit code; deliveries start without one (after the pickup photo). */
export const StartRequest = Type.Object({ boardingCode: Type.Optional(Type.String({ pattern: "^[0-9]{4}$" })) }, { $id: "StartRequest" });
export const RatingRequest = Type.Object(
  { stars: Type.Integer({ minimum: 1, maximum: 5 }), tags: Type.Optional(Type.Array(Type.String({ maxLength: 40 }), { maxItems: 8 })), comment: Type.Optional(Type.String({ maxLength: 500 })) },
  { $id: "RatingRequest" },
);
export const PlaceKind = Type.Union([Type.Literal("address"), Type.Literal("establishment"), Type.Literal("route"), Type.Literal("area")], { $id: "PlaceKind" });
export const PlaceSuggestion = Type.Object(
  {
    placeId: Type.String(),
    /** "Shopping Eldorado" / "Av. Paulista, 1578" */
    primary: Type.String(),
    /** "Av. Rebouças, 3970 - Pinheiros, São Paulo - SP" */
    secondary: Type.String(),
    /** From the rider's position, when lat/lng were sent. */
    distanceMeters: Type.Optional(Type.Integer()),
    kind: PlaceKind,
  },
  { $id: "PlaceSuggestion" },
);

/** Brazilian address parts; any may be missing. */
export const AddressComponents = Type.Object(
  {
    street: Type.Optional(Type.String()),
    number: Type.Optional(Type.String()),
    neighborhood: Type.Optional(Type.String()),
    city: Type.Optional(Type.String()),
    state: Type.Optional(Type.String({ description: "UF, e.g. SP" })),
    postalCode: Type.Optional(Type.String({ description: "01310-200" })),
  },
  { $id: "AddressComponents" },
);

/**
 * A place ready to show and book. Send `{ address, location, placeId }` as the trip's Place;
 * show `title` / `subtitle`. When `hasStreetNumber` is false, ask for the number and resolve again
 * with GET /places/geocode.
 */
export const ResolvedPlace = Type.Object(
  {
    placeId: Type.Optional(Type.String()),
    location: LatLng,
    address: Type.String({ description: "One line stored on trips: \"Av. Paulista, 1578 — Bela Vista, São Paulo - SP\"" }),
    title: Type.String(),
    subtitle: Type.String(),
    components: AddressComponents,
    hasStreetNumber: Type.Boolean(),
    kind: PlaceKind,
    formattedAddress: Type.Optional(Type.String()),
  },
  { $id: "ResolvedPlace" },
);
/**
 * `GET /trips/{id}/route`: the driver's street route for the current leg, from their last known
 * position to the pickup (assigned / arrived) or the dropoff (in progress). Recomputed at most every
 * minute, or sooner when the driver leaves it; apps trim it from the car's live position.
 */
export const TripRoute = Type.Object(
  {
    target: Type.Union([Type.Literal("pickup"), Type.Literal("stop"), Type.Literal("dropoff")]),
    /** target "stop": which stop (1-based). */
    stopSeq: Type.Optional(Type.Integer({ minimum: 1 })),
    polyline: Type.Union([Type.String(), Type.Null()]),
    distanceMeters: Type.Integer(),
    durationSeconds: Type.Integer(),
  },
  { $id: "TripRoute" },
);
export type TripRoute = Static<typeof TripRoute>;
/**
 * `GET /driver/offers/current`: the caller's pending offer, the same data as `trip.offered`, for an
 * offer that arrived only by push (app killed, socket down). Null when there is none or it expired.
 */
/** `GET /driver/open-trips`: Corridas abertas near the driver, nearest first. */
export const OpenTripsList = Type.Object({ trips: Type.Array(OpenTrip) }, { $id: "OpenTripsList" });
export const CurrentOffer = Type.Object({ offer: Type.Union([TripOffered.properties.data, Type.Null()]) }, { $id: "CurrentOffer" });
export const ApiError = Type.Object({ error: Type.String(), message: Type.Optional(Type.String()) }, { $id: "ApiError" });

// ── Rider (Cliente-03/07/08/09/10/11) ─────────────────────────────────────────────────────────
export const TripHistoryItem = Type.Object(
  {
    id: Type.String(),
    code: Type.String(),
    status: TripStatus,
    kind: TripKind,
    category: RideCategory,
    categoryName: Type.String(),
    pickupAddress: Type.String(),
    dropoffAddress: Type.String(),
    chargedCents: Cents,
    requestedAt: Type.String({ format: "date-time" }),
    completedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    driver: Type.Union([Type.Object({ firstName: Type.String(), vehicle: Type.String(), plate: Type.String() }), Type.Null()]),
    scheduledFor: Type.Optional(Type.Union([Type.String({ format: "date-time" }), Type.Null()])),
    tipCents: Type.Optional(Cents),
    paymentMethod: Type.Optional(PaymentMethod),
  },
  { $id: "TripHistoryItem" },
);
export const TripHistory = Type.Object(
  {
    items: Type.Array(TripHistoryItem),
    nextCursor: Type.Union([Type.String(), Type.Null()]),
    month: Type.Object({ startsAt: Type.String({ format: "date-time" }), totalCents: Cents, trips: Type.Integer() }),
  },
  { $id: "TripHistory" },
);
export const RecentPlace = Type.Object({ address: Type.String(), location: LatLng, lastUsedAt: Type.String({ format: "date-time" }) }, { $id: "RecentPlace" });

export const PlaceLabel = Type.Union([Type.Literal("casa"), Type.Literal("trabalho"), Type.Literal("outro")], { $id: "PlaceLabel" });
export const SavedPlace = Type.Object(
  {
    id: Type.String(),
    label: PlaceLabel,
    name: Type.Union([Type.String(), Type.Null()]),
    address: Type.String(),
    complement: Type.Union([Type.String(), Type.Null()]),
    instructions: Type.Union([Type.String(), Type.Null()]),
    location: LatLng,
    /** Structured parts when the address was picked/resolved in the app (CEP, número…). */
    components: Type.Union([AddressComponents, Type.Null()]),
    placeId: Type.Union([Type.String(), Type.Null()]),
    isDefault: Type.Boolean(),
  },
  { $id: "SavedPlace" },
);
export const SavedPlaceInput = Type.Object(
  {
    label: PlaceLabel,
    name: Type.Optional(Type.Union([Type.String({ maxLength: 40 }), Type.Null()])),
    address: Type.String({ minLength: 1, maxLength: 300 }),
    complement: Type.Optional(Type.Union([Type.String({ maxLength: 120 }), Type.Null()])),
    instructions: Type.Optional(Type.Union([Type.String({ maxLength: 200 }), Type.Null()])),
    location: LatLng,
    components: Type.Optional(Type.Union([AddressComponents, Type.Null()])),
    placeId: Type.Optional(Type.Union([Type.String({ maxLength: 300 }), Type.Null()])),
    isDefault: Type.Optional(Type.Boolean()),
  },
  { $id: "SavedPlaceInput" },
);
export const SavedPlaceUpdate = Type.Partial(SavedPlaceInput, { $id: "SavedPlaceUpdate" });

export const PaymentView = Type.Object(
  {
    status: Type.Union([Type.Literal("pending"), Type.Literal("approved"), Type.Literal("expired"), Type.Literal("failed"), Type.Literal("refunded"), Type.Literal("partially_refunded")]),
    /** cash: Pagar na hora, received in hand by the driver. */
    method: Type.Union([Type.Literal("pix"), Type.Literal("card"), Type.Literal("cash")]),
    amountCents: Cents,
    refundedCents: Cents,
    pix: Type.Union([PixPayment, Type.Null()]),
    paidAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { $id: "PaymentView" },
);

export const NearbyCar = Type.Object(
  { location: LatLng, heading: Type.Union([Type.Number(), Type.Null()]), category: RideCategory, vehicleKind: VehicleKind },
  { $id: "NearbyCar" },
);

export const ChatMessageView = Type.Object(
  {
    id: Type.String(),
    senderId: Type.String(),
    senderRole: Type.Union([Type.Literal("rider"), Type.Literal("driver")]),
    body: Type.String(),
    sentAt: Type.String({ format: "date-time" }),
    readAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { $id: "ChatMessageView" },
);
export const ChatSendRequest = Type.Object(
  { id: Type.String({ format: "uuid", description: "Client-generated; retries with the same id are no-ops" }), body: Type.String({ minLength: 1, maxLength: 1000 }) },
  { $id: "ChatSendRequest" },
);
export const ChatReadRequest = Type.Object({ upToMessageId: Type.String() }, { $id: "ChatReadRequest" });

export const MeView = Type.Object(
  {
    id: Type.String(),
    name: Type.String(),
    /** True while the name is still the phone placeholder: ask "Como podemos te chamar?". */
    needsName: Type.Boolean(),
    email: Type.Union([Type.String(), Type.Null()]),
    phoneNumber: Type.Union([Type.String(), Type.Null()]),
    cpf: Type.Union([Type.String(), Type.Null()]),
    rider: Type.Object({ rating: Type.Number(), completedTrips: Type.Integer() }),
    driver: Type.Union([Type.Object({ reviewStatus: Type.String(), cityId: Type.String() }), Type.Null()]),
    emailVerified: Type.Optional(Type.Boolean()),
    /** How this account can sign in ("Formas de entrar"). */
    signInMethods: Type.Optional(Type.Array(SignInMethod)),
    /** Self-declared; unlocks trip types limited by gender (docs/specs/trip-types.md). */
    gender: Type.Union([Gender, Type.Null()]),
    /** The avatar as a short-lived link (ADR 0012), null without one: show initials. */
    photoUrl: Type.Optional(Type.Union([Type.String(), Type.Null()])),
  },
  { $id: "MeView" },
);
export const ProfileUpdate = Type.Object(
  {
    name: Type.Optional(Type.String({ minLength: 2, maxLength: 80 })),
    cpf: Type.Optional(Type.String({ minLength: 11, maxLength: 14 })),
    /** null clears it ("Prefiro não dizer"). */
    gender: Type.Optional(Type.Union([Gender, Type.Null()])),
  },
  { $id: "ProfileUpdate" },
);

/** `GET /trips/pending-rating`: the caller's latest completed trip of the last 24 h they have not rated. */
export const PendingRating = Type.Object({ trip: Type.Union([TripView, Type.Null()]) }, { $id: "PendingRating" });

// ── Privacidade e LGPD (Cliente-10) ─────────────────────────────────────────────────────
export const PrivacySummary = Type.Object(
  {
    account: Type.Object({
      name: Type.String(),
      phoneMasked: Type.Union([Type.String(), Type.Null()]),
      email: Type.Union([Type.String(), Type.Null()]),
      cpfMasked: Type.Union([Type.String(), Type.Null()]),
      createdAt: Type.String({ format: "date-time" }),
    }),
    /** What we hold about the user, by kind. */
    data: Type.Array(Type.Object({ id: Type.String(), title: Type.String(), count: Type.Integer(), retention: Type.String({ description: "pt-BR, e.g. \"5 anos (obrigação fiscal)\"" }) })),
    /** Open deletion request, if any ("Excluir minha conta"). */
    deletionRequest: Type.Union([
      Type.Object({
        ticketId: Type.String(),
        protocol: Type.String(),
        requestedAt: Type.String({ format: "date-time" }),
        /** When the account is anonymized (grace period), and whether something blocks it. */
        scheduledFor: Type.Optional(Type.String({ format: "date-time" })),
        status: Type.Optional(Type.Union([Type.Literal("pending"), Type.Literal("blocked")])),
        blockedReason: Type.Optional(Type.Union([Type.String(), Type.Null()])),
      }),
      Type.Null(),
    ]),
    policyUrl: Type.String(),
  },
  { $id: "PrivacySummary" },
);
export const DeletionRequestInput = Type.Object(
  {
    reason: Type.Optional(Type.String({ maxLength: 500 })),
    /** The app asking; the ticket lands in that app's inbox (rider when absent, as older apps send). */
    app: Type.Optional(SupportApp),
  },
  { $id: "DeletionRequestInput" },
);
